<template>
  <div>
    <!-- Backdrop: mobile-only (desktop layout doesn't reflow content, so no need to dim it there) -->
    <div v-if="isOpen" class="ad-backdrop d-lg-none" @click="close"></div>

    <aside class="ad-panel" :class="{ 'ad-panel--open': isOpen }">
      <div class="ad-header">
        <div class="ad-header-main">
          <i class="fas fa-robot ad-header-ico"></i>
          <span class="ad-header-title">{{ $t('Assistant.Title') }}</span>
        </div>
        <div class="ad-header-actions">
          <div class="dropdown">
            <button
              type="button"
              class="ad-icon-btn"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              :title="$t('Assistant.History')"
            >
              <i class="fas fa-clock-rotate-left"></i>
            </button>
            <ul class="dropdown-menu dropdown-menu-end ad-history-menu">
              <li>
                <button type="button" class="dropdown-item" @click="onNewChat">
                  <i class="fas fa-plus me-2"></i>{{ $t('Assistant.NewChat') }}
                </button>
              </li>
              <li v-if="conversationList.length"><hr class="dropdown-divider" /></li>
              <li v-for="c in conversationList" :key="c._id">
                <button
                  type="button"
                  class="dropdown-item"
                  :class="{ 'ad-history-item--active': c._id === conversationId }"
                  @click="onOpenConversation(c._id)"
                >{{ c.title || $t('Assistant.UntitledChat') }}</button>
              </li>
              <li v-if="!conversationList.length">
                <span class="dropdown-item disabled">{{ $t('Assistant.NoHistory') }}</span>
              </li>
            </ul>
          </div>
          <router-link :to="{ name: 'Assistant' }" class="ad-icon-btn" :title="$t('Assistant.OpenFullPage')" @click="close">
            <i class="fas fa-up-right-and-down-left-from-center"></i>
          </router-link>
          <button type="button" class="ad-icon-btn" :aria-label="$t('General.Close')" @click="close">
            <i class="fas fa-xmark"></i>
          </button>
        </div>
      </div>

      <AssistantChatBody v-if="hasOpenedOnce" class="ad-body" />
    </aside>
  </div>
</template>

<script>
import AssistantChatBody from '@/views/assistant/AssistantChatBody.vue'
import { useAssistantChat } from '@/views/assistant/useAssistantChat'
import { useAssistantDrawer } from '@/composables/useAssistantDrawer'

export default {
  name: 'AssistantDrawer',
  components: { AssistantChatBody },
  setup() {
    const chat = useAssistantChat()
    const drawer = useAssistantDrawer()
    return { ...chat, ...drawer }
  },
  data() {
    return { hasOpenedOnce: false }
  },
  watch: {
    isOpen(val) {
      if (val) this.hasOpenedOnce = true
    },
    '$route.name'(name) {
      // The full /assistant page already renders the same conversation — keeping the drawer
      // open at the same time would just show the identical thread twice.
      if (name === 'Assistant') this.close()
    },
  },
  methods: {
    onNewChat() {
      if (this.busy) return
      this.newChat()
    },
    async onOpenConversation(id) {
      if (this.busy) return
      await this.openConversation(id)
    },
  },
}
</script>

<style scoped>
.ad-backdrop {
  position: fixed; inset: 0; background: rgba(0, 0, 0, 0.35); z-index: 1599;
}

.ad-panel {
  position: fixed; top: 0; right: 0; bottom: 0; z-index: 1600;
  width: 380px; max-width: 92vw;
  display: flex; flex-direction: column;
  background: var(--ui-bg-card); border-left: 1px solid var(--ui-border);
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.12);
  transform: translateX(100%);
  transition: transform 0.25s ease;
}
.ad-panel--open { transform: translateX(0); }

.ad-header {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 12px 14px; border-bottom: 1px solid var(--ui-border); flex: 0 0 auto;
}
.ad-header-main { display: flex; align-items: center; gap: 8px; min-width: 0; }
.ad-header-ico { color: var(--c-primary-500); font-size: 15px; }
.ad-header-title { font: 700 14px 'Open Sans', sans-serif; color: var(--ui-text-1); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.ad-header-actions { display: flex; align-items: center; gap: 6px; flex: 0 0 auto; }

.ad-icon-btn {
  display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px;
  border: none; background: transparent; color: var(--ui-text-3); border-radius: var(--radius-md);
  cursor: pointer; font-size: 13px; text-decoration: none;
}
.ad-icon-btn:hover { background: var(--ui-bg-sunken); color: var(--ui-text-1); }

.ad-history-menu { min-width: 220px; max-height: 320px; overflow-y: auto; }
.ad-history-item--active { font-weight: 700; color: var(--c-primary-500); }

.ad-body { flex: 1; min-height: 0; }
</style>
